// Named `QuickStart` for history; the visible heading is "Recommended actions".
// Presentational only: navigation and modals are wired by the page through the
// handler props. No row may carry a resting highlight; rows tint only on hover.
import {
  Card,
  CardContent,
  CardHeader,
  Item,
  ItemActions,
  ItemSeparator,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from '@monksflow/monks-ui';
import {
  ChevronRightIcon,
  FilePlus2Icon,
  FolderPlusIcon,
  UploadIcon,
  UserRoundPlusIcon,
  type LucideIcon,
} from 'lucide-react';
import { Fragment } from 'react';
import { PermissionTooltip } from '@/shared/ui';
import { useAssetApprovalsHidden } from '@/shared/hooks/useAssetApprovalsHidden';
import { useTranslation } from 'react-i18next';

interface QuickStartProps {
  /** Invoked when the user clicks the "Create a brief" item. */
  onCreateBrief: () => void;
  /** Disables the "Create a brief" item with a permission tooltip. */
  createBriefDisabled?: boolean;
  /** Invoked when the user clicks the "Add artifacts to the project library" item. */
  onAddArtifacts: () => void;
  addArtifactsDisabled?: boolean;
  /** Invoked when the user clicks the "Invite collaborators" item. */
  onInviteCollaborators: () => void;
  inviteDisabled?: boolean;
  /** Invoked when the user clicks the "Upload assets for approvals" item. */
  onUploadAssetsForApproval: () => void;
  uploadForApprovalDisabled?: boolean;
}

interface QuickStartItem {
  icon: LucideIcon;
  title: string;
  description: string;
  onClick: () => void;
  disabled: boolean;
}

export function QuickStart(props: QuickStartProps) {
  const { t } = useTranslation('projects');
  const assetApprovalsHidden = useAssetApprovalsHidden();

  const items: QuickStartItem[] = [
    {
      icon: FilePlus2Icon,
      title: t('projectOverview.quickStart.createABrief'),
      description: t('projectOverview.quickStart.agenticallyCreateABriefBased'),
      onClick: props.onCreateBrief,
      disabled: !!props.createBriefDisabled,
    },
    {
      icon: FolderPlusIcon,
      title: t('projectOverview.quickStart.addArtifactsToTheProject'),
      description: t('projectOverview.quickStart.bringInKeyArtifactsFrom'),
      onClick: props.onAddArtifacts,
      disabled: !!props.addArtifactsDisabled,
    },
    {
      icon: UserRoundPlusIcon,
      title: t('projectOverview.quickStart.inviteCollaborators'),
      description: t('projectOverview.quickStart.addTeamMembersSoThey'),
      onClick: props.onInviteCollaborators,
      disabled: !!props.inviteDisabled,
    },
    ...(assetApprovalsHidden
      ? []
      : [
          {
            icon: UploadIcon,
            title: t('projectOverview.quickStart.uploadAssetsForApprovals'),
            description: t('projectOverview.quickStart.submitCreativeAssetsForTeam'),
            onClick: props.onUploadAssetsForApproval,
            disabled: !!props.uploadForApprovalDisabled,
          },
        ]),
  ];

  return (
    <Card compact className="h-full gap-md p-xl shadow-none!">
      {/* No icon beside the heading: the design leads with the words. */}
      <CardHeader
        // The header grid reserves a second row for a description this card
        // does not have, so its gap shows up as space under the heading.
        className="grid-rows-[auto] gap-0 p-0"
      >
        <h2 className="type-heading-md leading-[26px] text-foreground">
          {t('projectOverview.quickStart.recommendedActions')}
        </h2>
      </CardHeader>

      <CardContent className="p-0">
        <ItemGroup>
          {items.map((item, index) => {
            const Icon = item.icon;
            return (
              <Fragment key={item.title}>
                {index > 0 && <ItemSeparator />}
                <PermissionTooltip disabled={item.disabled}>
                  <Item
                    asChild
                    // `size` offers 16px or 12px insets but not the 12px-with-16px-gap
                    // this row draws, so the padding is set here and the gap left alone.
                    // The ring paints a pixel outside the row, exactly where the next
                    // separator sits — and the separator, painted later, cuts it off.
                    // Lifting the focused row puts the whole ring on top.
                    // `ghost-hover` rather than `muted`: muted is the card colour in
                    // dark mode, so the hover would not show there.
                    className="relative min-h-[82px] gap-md p-sm hover:bg-ghost-hover focus-visible:z-10 focus-visible:border-transparent disabled:pointer-events-none disabled:opacity-50 disabled:hover:bg-transparent"
                  >
                    <button
                      type="button"
                      onClick={item.onClick}
                      disabled={item.disabled}
                      className="text-left"
                    >
                      {/* The primitive top-aligns its media and nudges it 2px for a
                          multi-line item; the design centres it against the row. */}
                      <ItemMedia className="size-10 rounded-sm bg-secondary group-has-[[data-slot=item-description]]/item:translate-y-0 group-has-[[data-slot=item-description]]/item:self-center">
                        <Icon className="size-5 text-foreground-subtle" />
                      </ItemMedia>
                      <ItemContent className="gap-3xs">
                        {/* `text-base`, not `text-body-lg`: tailwind-merge reads the design
                            system's own type names as colours, so the token would
                            drop `text-foreground` and lose to the primitive's
                            `text-sm`. Both resolve to 16px/24px. */}
                        <ItemTitle className="text-base font-medium text-foreground">
                          {item.title}
                        </ItemTitle>
                        <ItemDescription className="text-sm leading-5">
                          {item.description}
                        </ItemDescription>
                      </ItemContent>
                      {!item.disabled && (
                        <ItemActions className="self-center group-has-[[data-slot=item-description]]/item:self-center">
                          <ChevronRightIcon
                            aria-hidden
                            data-testid="quick-start-row-chevron"
                            className="size-4 text-foreground-subtle transition-colors duration-fast ease-out group-hover/item:text-foreground group-focus-visible/item:text-foreground"
                          />
                        </ItemActions>
                      )}
                    </button>
                  </Item>
                </PermissionTooltip>
              </Fragment>
            );
          })}
        </ItemGroup>
      </CardContent>
    </Card>
  );
}
